/**
 * BANK-IBAN-R3.7 — checkout contact/delivery profile resolution.
 * Reuses the existing Customer Cabinet address book. Does not invent addresses.
 */
import type { CustomerSavedAddressV2 } from "./customer-profile-v2.js";
import { isValidPersonName, isValidUaPhone, normalizeUaPhone } from "./checkout-validation-v2.js";
import type { NovaPoshtaReferenceSelection } from "./nova-poshta-reference-v1.js";

const SESSION_PROFILE_KEY = "asiastyle.store-v2.checkout-session-profile.v1";

export function readCheckoutSessionProfileId(): string | null {
  try {
    const id = sessionStorage.getItem(SESSION_PROFILE_KEY);
    return id && id.trim() ? id.trim().slice(0, 40) : null;
  } catch {
    return null;
  }
}

export function writeCheckoutSessionProfileId(id: string | null): void {
  try {
    if (!id) sessionStorage.removeItem(SESSION_PROFILE_KEY);
    else sessionStorage.setItem(SESSION_PROFILE_KEY, id.slice(0, 40));
  } catch {
    /* ignore */
  }
}

export function formatUaPhoneDisplay(phone: string): string {
  const e164 = normalizeUaPhone(phone);
  if (!e164) return phone.trim();
  return `${e164.slice(0, 4)} ${e164.slice(4, 6)} ${e164.slice(6, 9)} ${e164.slice(9, 11)} ${e164.slice(11)}`;
}

export function isSavedAddressContactComplete(addr: Pick<CustomerSavedAddressV2, "name" | "surname" | "phone">): boolean {
  return isValidPersonName(addr.name) && isValidPersonName(addr.surname) && isValidUaPhone(addr.phone);
}

export function isSavedAddressDeliveryComplete(addr: CustomerSavedAddressV2): boolean {
  const type = addr.delivery_type ?? "NOVA_POSHTA_BRANCH";
  const settlement = String(addr.settlement_ref ?? addr.np_selection?.settlementRef ?? "").trim();
  const city = String(addr.city ?? addr.np_selection?.settlementLabel ?? "").trim();
  if (!settlement && !city) return false;
  if (type === "NOVA_POSHTA_COURIER") {
    return Boolean(String(addr.street ?? addr.np_selection?.addressLine ?? "").trim());
  }
  const destRef = String(
    addr.branch_ref ?? addr.locker_ref ?? addr.np_selection?.warehouseRef ?? "",
  ).trim();
  const destLabel = String(
    addr.warehouse ?? addr.locker_number ?? addr.branch_number ?? addr.np_selection?.warehouseLabel ?? "",
  ).trim();
  return Boolean(destRef || destLabel);
}

export function isSavedAddressCheckoutReady(addr: CustomerSavedAddressV2): boolean {
  return isSavedAddressContactComplete(addr) && isSavedAddressDeliveryComplete(addr);
}

export function isCheckoutContactDeliveryComplete(input: {
  name: string;
  surname: string;
  phone: string;
  city: string;
  warehouse: string;
  npSelection: NovaPoshtaReferenceSelection | null;
  npRefEnabled: boolean;
  npManualFallback: boolean;
}): boolean {
  if (!isValidPersonName(input.name) || !isValidPersonName(input.surname) || !isValidUaPhone(input.phone)) {
    return false;
  }
  if (input.npRefEnabled && !input.npManualFallback) {
    const sel = input.npSelection;
    if (!sel?.settlementRef) return false;
    if (sel.mode === "address") {
      return Boolean(String(sel.building ?? "").trim() || String(sel.addressLine ?? "").trim());
    }
    return Boolean(String(sel.warehouseRef ?? "").trim());
  }
  return Boolean(input.city.trim() && input.warehouse.trim());
}

export type CheckoutDeliveryResolution =
  | { status: "selected"; address: CustomerSavedAddressV2; source: "session" | "default" | "only" }
  | { status: "needs_selection"; addresses: CustomerSavedAddressV2[] }
  | {
      status: "needs_editor";
      reason: "none" | "incomplete_contact" | "incomplete_delivery" | "contact_only";
      contact?: CustomerSavedAddressV2;
    };

/**
 * Resolution priority:
 * 1. session-selected profile for this checkout visit
 * 2. explicitly marked default/preferred
 * 3. exactly one valid saved profile
 * 4. do not auto-pick an arbitrary address among several without a default
 */
export function resolveCheckoutDeliveryProfile(input: {
  addresses: CustomerSavedAddressV2[];
  sessionSelectedId?: string | null;
}): CheckoutDeliveryResolution {
  const active = input.addresses.filter((a) => !a.archived);
  const ready = active.filter(isSavedAddressCheckoutReady);
  const sessionId = String(input.sessionSelectedId ?? "").trim();
  if (sessionId) {
    const sessionAddr = active.find((a) => a.id === sessionId);
    if (sessionAddr && isSavedAddressCheckoutReady(sessionAddr)) {
      return { status: "selected", address: sessionAddr, source: "session" };
    }
  }
  const defaults = ready.filter((a) => a.is_default);
  if (defaults.length === 1 && defaults[0]) {
    return { status: "selected", address: defaults[0], source: "default" };
  }
  if (ready.length === 1 && ready[0]) {
    return { status: "selected", address: ready[0], source: "only" };
  }
  if (ready.length > 1) {
    return { status: "needs_selection", addresses: ready };
  }
  const contact = active.find(isSavedAddressContactComplete);
  if (active.length === 0) return { status: "needs_editor", reason: "none" };
  const hasDelivery = active.some(isSavedAddressDeliveryComplete);
  if (contact && !hasDelivery) return { status: "needs_editor", reason: "contact_only", contact };
  if (hasDelivery && !contact) return { status: "needs_editor", reason: "incomplete_contact" };
  return { status: "needs_editor", reason: "incomplete_delivery", ...(contact ? { contact } : {}) };
}

export function savedAddressIdentityKey(addr: CustomerSavedAddressV2): string {
  const type = addr.delivery_type ?? "NOVA_POSHTA_BRANCH";
  const settlement = String(addr.settlement_ref ?? addr.np_selection?.settlementRef ?? "")
    .trim()
    .toLowerCase();
  const destRef = String(
    addr.branch_ref ?? addr.locker_ref ?? addr.np_selection?.warehouseRef ?? "",
  )
    .trim()
    .toLowerCase();
  if (settlement && destRef) return `${type}:${settlement}:${destRef}`;
  if (type === "NOVA_POSHTA_COURIER") {
    const street = [
      addr.street ?? addr.np_selection?.addressLine ?? "",
      addr.building ?? addr.np_selection?.building ?? "",
      addr.apartment ?? addr.np_selection?.flat ?? "",
    ]
      .join("|")
      .trim()
      .toLowerCase();
    const cityKey = settlement || String(addr.city ?? "").trim().toLowerCase();
    return `${type}:${cityKey}:${street}`;
  }
  const city = String(addr.city ?? "").trim().toLowerCase();
  const warehouse = String(addr.warehouse ?? "").trim().toLowerCase();
  return `${type}:${city}:${warehouse}`;
}

export function findDuplicateSavedAddress(
  book: CustomerSavedAddressV2[],
  candidate: CustomerSavedAddressV2,
): CustomerSavedAddressV2 | undefined {
  const key = savedAddressIdentityKey(candidate);
  return book.find((a) => a.id !== candidate.id && !a.archived && savedAddressIdentityKey(a) === key);
}
